import { createContext, useContext, useState, type ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: number;
  title: string;
  description: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (title: string, description: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = (title: string, description: string, type: ToastType = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, title, description, type }]);
    
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      
      {/* Global Toast Layer */}
      <div className="fixed top-8 right-8 z-[200] flex flex-col gap-4 pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className="animate-in fade-in slide-in-from-top-4 duration-300 pointer-events-auto origin-top">
            <div className={`p-5 rounded-3xl shadow-xl border flex gap-4 min-w-[320px] max-w-sm overflow-hidden relative backdrop-blur-2xl ${
              toast.type === 'success' ? 'bg-emerald-50/90 dark:bg-emerald-950/40 border-emerald-500/20' :
              toast.type === 'error' ? 'bg-red-50/90 dark:bg-red-950/40 border-red-500/20' :
              toast.type === 'warning' ? 'bg-amber-50/90 dark:bg-amber-950/40 border-amber-500/20' :
              'bg-blue-50/90 dark:bg-blue-950/40 border-blue-500/20'
            }`}>
              <div className={`shrink-0 mt-0.5 w-8 h-8 flex items-center justify-center ${
                toast.type === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 
                toast.type === 'error' ? 'text-red-600 dark:text-red-400' :
                toast.type === 'warning' ? 'text-amber-600 dark:text-amber-400' :
                'text-blue-600 dark:text-blue-400'
              }`}>
                <span className="material-symbols-outlined text-2xl font-normal">
                  {toast.type === 'success' ? 'check_circle' : 
                   toast.type === 'error' ? 'error' :
                   toast.type === 'warning' ? 'warning' :
                   'info'}
                </span>
              </div>
              <div className="space-y-1 relative z-10 pt-1 flex-1">
                <p className={`text-[12px] font-black uppercase tracking-[0.1em] ${
                  toast.type === 'success' ? 'text-emerald-800 dark:text-emerald-300' : 
                  toast.type === 'error' ? 'text-red-800 dark:text-red-300' :
                  toast.type === 'warning' ? 'text-amber-800 dark:text-amber-300' :
                  'text-blue-800 dark:text-blue-300'
                }`}>
                  {toast.title}
                </p>
                <p className={`text-xs font-bold leading-relaxed tracking-tight ${
                  toast.type === 'success' ? 'text-emerald-700/80 dark:text-emerald-200/60' : 
                  toast.type === 'error' ? 'text-red-700/80 dark:text-red-200/60' :
                  toast.type === 'warning' ? 'text-amber-700/80 dark:text-amber-200/60' :
                  'text-blue-700/80 dark:text-blue-200/60'
                }`}>
                  {toast.description}
                </p>
              </div>
              {/* Soft Ambient Corner Fade */}
              <div className={`absolute -bottom-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-20 pointer-events-none ${
                toast.type === 'success' ? 'bg-emerald-400' : 
                toast.type === 'error' ? 'bg-red-400' :
                toast.type === 'warning' ? 'bg-amber-400' :
                'bg-blue-400'
              }`} />
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
